import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete, Box, Button, CardContent, Chip, Divider,
    Grid, TextField, Typography
} from "@mui/material";
import {DateRange} from "@iroomit/react-date-range";
import { useEffect, useRef } from "react";
import { Filter, X } from "lucide-react";
import { LuCalendarRange } from "react-icons/lu";
import SoftInput from "components/SoftInput";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import CustomSelect from "../../reusableComponents/Select/CustomSelect";
import { ar, enUS } from "date-fns/locale";
import { format } from "date-fns";
import Card from "@mui/material/Card";
import PropTypes from "prop-types";

const FiltersCard = ({
                         emailFilter,
                         statusFilter,
                         dateFilter,
                         allReportersEmails,
                         onEmailFilterChange,
                         onStatusFilterChange,
                         onDateFilterChange,
                         onClearFilters
                     }) => {
    const { t, i18n } = useTranslation(["reportIssue", "common"]);
    const isRTL = i18n.dir() === "rtl";

    const [tempRange, setTempRange] = useState({
        startDate: null,
        endDate: null,
    });
    const [datePlaceholder, setDatePlaceholder] = useState(t('filters.date.placeholder', { ns: 'common' }));
    const [showDatePicker, setShowDatePicker] = useState(false);

    const inputRef = useRef(null);
    const pickerRef = useRef(null);

    const statusOptions = [
        { id: 'all', name: t('All') },
        { id: 'ToDo', name: t('ToDo') },
        { id: 'Running', name: t('Running') },
        { id: 'Postponed', name: t('Postponed') },
        { id: 'Cancelled', name: t('Cancelled') },
        { id: 'Completed', name: t('Completed') },
    ];

    const handleOpenDatePicker = () => {
        setShowDatePicker(true);
    };

    const handleCloseDatePicker = () => setShowDatePicker(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                pickerRef.current &&
                !pickerRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                handleCloseDatePicker();
            }
        };
        if (showDatePicker) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showDatePicker]);

    const handleDateClear = () => {
        setTempRange({ startDate: null, endDate: null });
        onDateFilterChange({ start_date: null, end_date: null });
        setDatePlaceholder(t('filters.date.placeholder', { ns: 'common' }));
        setShowDatePicker(false);
    };

    const handleDateSelect = (ranges) => {
        const { startDate, endDate } = ranges.selection;
        setTempRange({ startDate, endDate });
    };

    const handleDateApply = () => {
        if (tempRange.startDate && tempRange.endDate) {
            const newDateFilter = {
                start_date: format(tempRange.startDate, 'yyyy-MM-dd'),
                end_date: format(tempRange.endDate, 'yyyy-MM-dd'),
            };
            onDateFilterChange(newDateFilter);
            setDatePlaceholder(`${format(tempRange.startDate, 'yyyy-MM-dd')} - ${format(tempRange.endDate, 'yyyy-MM-dd')}`);
        } else {
            setDatePlaceholder(t('filters.date.placeholder', { ns: 'common' }));
        }
        setShowDatePicker(false);
    };

    const hasActiveFilters = emailFilter !== "" || statusFilter !== "all" || (dateFilter.start_date !== null && dateFilter.end_date !== null);

    const handleEmailFilterClear = () => onEmailFilterChange("");
    const handleStatusFilterClear = () => onStatusFilterChange("all");

    // Update date placeholder when dateFilter changes
    useEffect(() => {
        if (dateFilter.start_date && dateFilter.end_date) {
            setDatePlaceholder(`${dateFilter.start_date} - ${dateFilter.end_date}`);
        } else {
            setDatePlaceholder(t('filters.date.placeholder', { ns: 'common' }));
        }
    }, [dateFilter, t]);

    return (
        <Card sx={{ mb: 3, boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)", overflow: 'visible', position: 'relative' }}>
            <CardContent sx={{ p: 3 }}>
                {/* Filter Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Filter size={16} color="#6b7280" />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                        {t('Filters')}
                    </Typography>
                    {hasActiveFilters && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onClearFilters}
                            sx={{
                                ...(isRTL ? { marginRight: "auto" } : { marginLeft: "auto" }),
                                color: "#6b7280",
                                textTransform: "none",
                                fontSize: "0.875rem",
                                borderColor: "#6b7280",
                                minWidth: "auto",
                                padding: "4px 8px",
                                "&:hover": {
                                    color: "#374151",
                                    backgroundColor: "transparent",
                                },
                            }}
                        >
                            <X size={16} style={{ marginRight: 4 }} />
                            {t('clearAll')}
                        </Button>
                    )}
                </Box>

                {/* Filter Controls */}
                <Grid container spacing={2}>
                    {/* Email Filter */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: "0.875rem" }}>
                                {t('userEmail')}
                            </Typography>

                            <Autocomplete
                                options={allReportersEmails}
                                value={emailFilter}
                                onChange={(event, newValue) => onEmailFilterChange(newValue || "")}
                                clearOnEscape
                                freeSolo
                                size="medium"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('SearchOrSelectEmail')}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "white",
                                                "& fieldset": {
                                                    borderColor: "#d1d5db",
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "#9ca3af",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "#3b82f6",
                                                    borderWidth: "2px",
                                                },
                                                "& input": {
                                                    padding: "8px 12px",
                                                    fontSize: "0.875rem",
                                                    "&::placeholder": {
                                                        color: "#9ca3af",
                                                        opacity: 1,
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    // eslint-disable-next-line react/prop-types
                                    const { key, ...otherProps } = props;
                                    return (
                                        <Box component="li" key={key} {...otherProps} sx={{ fontSize: "0.875rem" }}>
                                            {option}
                                        </Box>
                                    );
                                }}
                                sx={{
                                    "& .MuiAutocomplete-popupIndicator": {
                                        color: "#6b7280",
                                    },
                                    "& .MuiAutocomplete-clearIndicator": {
                                        color: "#6b7280",
                                    },
                                    "& .MuiAutocomplete-endAdornment": {
                                        ...(isRTL
                                            ? {
                                                right: "unset !important",
                                                left: "9px",
                                            }
                                            : {
                                                right: "9px",
                                            }),
                                    },
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Status Filter */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: "0.875rem" }}>
                                {t('status')}
                            </Typography>

                            <CustomSelect
                                fullWidth
                                value={statusFilter}
                                onChange={onStatusFilterChange}
                                name="status"
                                options={statusOptions?.map((status) => ({
                                    value: status.id,
                                    label: status.name
                                }))}
                                dropdown={'bottom'}
                            />
                        </Box>
                    </Grid>

                    {/* Date Filter */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, position: "relative" }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", fontSize: "0.875rem" }}>
                                {t('filters.date.label', { ns: 'common' })}
                            </Typography>

                            <Box sx={{ position: "relative" }}>
                                <SoftInput
                                    ref={inputRef}
                                    type="text"
                                    placeholder={datePlaceholder}
                                    value={datePlaceholder}
                                    onClick={handleOpenDatePicker}
                                    readOnly
                                    fullWidth
                                />
                                <LuCalendarRange style={{
                                    position: 'absolute',
                                    ...(isRTL ? { left: "10px" } : { right: "10px" }),
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    color: '#6c757d',
                                }} />
                            </Box>

                            {showDatePicker && (
                                <Box
                                    ref={pickerRef}
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        zIndex: 10,
                                        backgroundColor: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                        mt: 1
                                    }}>
                                    <DateRange
                                        onChange={handleDateSelect}
                                        ranges={[{
                                            startDate: tempRange.startDate || new Date(),
                                            endDate: tempRange.endDate || new Date(),
                                            key: 'selection'
                                        }]}
                                        moveRangeOnFirstSelection={false}
                                        inputRanges={[]}
                                        editableDateInputs
                                        rangeColors={['#cb0c9f']}
                                        locale={i18n.language === 'ar' ? ar : enUS}
                                        direction={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                                    />
                                    <SoftBox display="flex" justifyContent="flex-end" p={1} gap={1} style={{ backgroundColor: "white" }}>
                                        <SoftButton variant="outlined" color="secondary" onClick={handleDateClear}>
                                            {t('filters.date.clear', { ns: 'common' })}
                                        </SoftButton>
                                        <SoftButton variant="gradient" color="light" onClick={handleDateApply}>
                                            {t('filters.date.apply', { ns: 'common' })}
                                        </SoftButton>
                                    </SoftBox>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {/* Active Filters Display */}
                {hasActiveFilters && (
                    <Box sx={{ mt: 2, pt: 2 }}>
                        <Divider sx={{ mb: 2, borderColor: "#e5e7eb" }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.875rem" }}>
                                {t("ActiveFilters")}
                            </Typography>
                            {emailFilter && (
                                <Chip
                                    label={`Email: ${emailFilter}`}
                                    size="small"
                                    variant="outlined"
                                    onDelete={handleEmailFilterClear}
                                    deleteIcon={<X size={12} />}
                                    sx={{
                                        backgroundColor: "#f3f4f6",
                                        borderColor: "#d1d5db",
                                        color: "#374151",
                                        fontSize: "0.75rem",
                                        height: 24,
                                        "& .MuiChip-label": {
                                            padding: "0 8px",
                                        },
                                        "& .MuiChip-deleteIcon": {
                                            color: "#6b7280",
                                            fontSize: "12px",
                                            ...(isRTL
                                                ? { marginLeft: "3px" }
                                                : { marginRight: "3px" }),
                                            "&:hover": {
                                                color: "#374151",
                                            },
                                        },
                                    }}
                                />
                            )}
                            {statusFilter !== "all" && (
                                <Chip
                                    label={`Status: ${statusFilter}`}
                                    size="small"
                                    variant="outlined"
                                    onDelete={handleStatusFilterClear}
                                    deleteIcon={<X size={12} />}
                                    sx={{
                                        backgroundColor: "#f3f4f6",
                                        borderColor: "#d1d5db",
                                        color: "#374151",
                                        fontSize: "0.75rem",
                                        height: 24,
                                        "& .MuiChip-label": {
                                            padding: "0 8px",
                                        },
                                        "& .MuiChip-deleteIcon": {
                                            color: "#6b7280",
                                            fontSize: "12px",
                                            "&:hover": {
                                                color: "#374151",
                                            },
                                        },
                                    }}
                                />
                            )}
                            {(dateFilter.start_date && dateFilter.end_date) && (
                                <Chip
                                    label={`Date: ${dateFilter.start_date} - ${dateFilter.end_date}`}
                                    size="small"
                                    variant="outlined"
                                    onDelete={handleDateClear}
                                    deleteIcon={<X size={12} />}
                                    sx={{
                                        backgroundColor: "#f3f4f6",
                                        borderColor: "#d1d5db",
                                        color: "#374151",
                                        fontSize: "0.75rem",
                                        height: 24,
                                        "& .MuiChip-label": {
                                            padding: "0 8px",
                                        },
                                        "& .MuiChip-deleteIcon": {
                                            color: "#6b7280",
                                            fontSize: "12px",
                                            "&:hover": {
                                                color: "#374151",
                                            },
                                        },
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

FiltersCard.propTypes = {
    emailFilter: PropTypes.string,
    statusFilter: PropTypes.string,
    dateFilter: PropTypes.shape({
        start_date: PropTypes.string,
        end_date: PropTypes.string,
    }).isRequired,
    allReportersEmails: PropTypes.arrayOf(PropTypes.string),
    onEmailFilterChange: PropTypes.func,
    onStatusFilterChange: PropTypes.func,
    onDateFilterChange: PropTypes.func,
    onClearFilters: PropTypes.func,
};

export default FiltersCard;
